// --- NEW FILE: packages/agent-kit/src/hooks/tasks/useTaskVersions.ts ---

import { useState, useCallback } from 'react';
import useSWR from 'swr';
import { adminTaskService, isApiError } from '@dopomogai/api-client';
import type { ProjectTask, TaskChangeLog, RevertPayload } from '@dopomogai/types';
import { AgentKitError } from '../../types';

/**
 * A hook for fetching the granular 'commit log' history of a single Task.
 * @param taskId The ID of the task. If undefined, no fetch occurs.
 */
export const useTaskVersionHistory = (taskId?: string) => {
  const swrKey = taskId ? ['/admin/task/history', taskId] : null;

  const { data, error, isLoading, mutate } = useSWR<TaskChangeLog[], AgentKitError>(
    swrKey,
    async ([, tId]: [string, string]) => {
      const response = await adminTaskService.getTaskHistory(tId);
      if (isApiError(response)) {
        throw new AgentKitError(`Failed to fetch task history for ${tId}`, response);
      }
      return response;
    }
  );

  return { 
    history: data, 
    error, 
    isLoading, 
    revalidate: mutate 
  };
};

/**
 * A hook that provides the function to revert a Task to a previous version.
 * This is a "mutation" hook and does not fetch data itself.
 */
export const useRevertTask = () => {
    const [isReverting, setIsReverting] = useState(false);
    const [error, setError] = useState<AgentKitError | null>(null);

    /**
     * Triggers the revert action for a single task.
     * @param taskId The ID of the task to revert.
     * @param payload The revert payload containing the target version and a commit message.
     * @returns The reverted ProjectTask object on success, or null on failure.
     */
    const revertTask = useCallback(async (taskId: string, payload: RevertPayload): Promise<ProjectTask | null> => {
        setIsReverting(true);
        setError(null);
        const res = await adminTaskService.revertTask(taskId, payload);
        if (isApiError(res)) {
            setError(new AgentKitError(`Failed to revert task ${taskId}`, res));
            setIsReverting(false);
            return null;
        }
        setIsReverting(false);
        return res;
    }, []);
    
    return { 
      revertTask, 
      isReverting, 
      error 
    };
};